/**
 * helper.tsx
 *
 * Utility functions and helper components.
 * For icon components, see @/utils/icons.tsx
 */

import DocxViewerWithHighlight, {
  addFuzzyHighlightToHtml,
  countScrollTargetMatches,
  highlightAllInHtml,
  highlightAllScrollTargetsInHtml,
  highlightTextInHtml,
} from '@/components/DocxViewerWithHighlight';
import PdfViewerWithHighlight from '@/components/PdfViewerWithHighlight';
import SearchPdfViewerWithHighlight from '@/components/SearchPdfViewerWithHighlight';
import { upload } from '@/services/api';
import { getIntl, useIntl } from '@umijs/max';
import { Card, message, Tooltip, Typography, UploadProps } from 'antd';
import mammoth from 'mammoth';
import MsgReader from 'msgreader';
import PostalMime from 'postal-mime';
import React, { FC, useEffect, useRef, useState } from 'react';
import Sanitize from 'sanitize-html';
import { FormattedMessage } from 'umi';
import { readDocxHeaderFooterHtml } from './docxHeaderFooter';

const { Text, Title } = Typography;

// Re-export icon components from icons.tsx for backward compatibility
export {
  AdminIcon,
  AnalysisIcon,
  ChatIcon,
  ChatIconFloating,
  ComparisonIcon,
  ComposerIcon,
  ContactIcon,
  DiagnosticsIcon,
  FolderIcon,
  GptIcon,
  GrammarOfUseIcon,
  HomeIcon,
  ICONS,
  LanguageIcon,
  LibraryIcon,
  LogoutIcon,
  ManagementIcon,
  MenuIcon,
  PlatformIcon,
  ProjectsHubIcon,
  QuoteIcon,
  ReportBugIcon,
  ReviewIcon,
  ScribeIcon,
  SearchIcon,
  SettingIcon,
  SideBySideIcon,
  TeamIcon,
  TemplateIcon,
  TranslationIcon,
  UserGuideIcon,
  WorkstudioIcon,
} from './icons';

// ===========================================================================
// LOCALIZATION UTILITIES
// ===========================================================================

export const Label: FC<{ language?: string; id: string; defaultMessage: string }> = (props) => {
  const { id, defaultMessage } = props;
  return <FormattedMessage id={id} defaultMessage={defaultMessage} />;
};

// ===========================================================================
// TEXT FORMATTING UTILITIES
// ===========================================================================

export const EntityText: FC<{ text: string }> = (props) => {
  const { text } = props;

  if (!text) return <></>;
  let displayText: any[] = [];
  const textComponents = text.split(/\{(.*)\}/g);
  textComponents?.forEach((value: any) => {
    if (value.match(/\{(.*)\}/g))
      displayText.push(<Text keyboard>{value.match(/\{(.*)\}/)[1]}</Text>);
    else displayText.push(value);
  });
  return (
    <>
      {displayText.map((item, index) => (
        <span key={index}>{item}</span>
      ))}
    </>
  );
};

export const EntityDisplayText = (text: string, entities: any) => {
  if (!text) return <></>;
  let displayText: any[] = [];
  const textComponents = text.split(/\{([a-f\d]{24})\}/g);
  textComponents?.map((value: any) => {
    if (entities?.[value])
      return displayText.push(<Text keyboard>{entities[value] || value}</Text>);
    else return displayText.push(value);
  });
  return <span>{displayText.map((item) => item)}</span>;
};

export const renderLabel = (label: string | undefined, searchQuery: string | undefined) => {
  if (searchQuery && label) {
    let index = label.toLowerCase().indexOf(searchQuery.toLowerCase());

    if (index !== -1) {
      let length = searchQuery.length;

      let prefix = label.substring(0, index);
      let suffix = label.substring(index + length);
      let match = label.substring(index, index + length);

      return (
        <span>
          {prefix}
          <Text mark>{match}</Text>
          {suffix}
        </span>
      );
    }
  }

  return <span>{label}</span>;
};

export const innerHTML = (text: string | undefined, searchQuery?: string) =>
  text && (
    <div
      style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
      dangerouslySetInnerHTML={{
        __html: text.replace(
          new RegExp(`${searchQuery}`),
          `<span style="background:yellow;color:black">${searchQuery}</span>`,
        ),
      }}
    />
  );

export const EllipsedText: React.FC<{
  text?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}> = ({ text, className, style }) => {
  const ref = React.useRef<HTMLSpanElement>(null);
  const [ellipsed, setEllipsed] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setEllipsed(el.scrollWidth > el.clientWidth);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [text]);

  const span = (
    <span
      ref={ref}
      className={className}
      style={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'block',
        ...(style || {}),
      }}
    >
      {text}
    </span>
  );
  return ellipsed ? <Tooltip title={text}>{span}</Tooltip> : span;
};

// ===========================================================================
// FILE UTILITIES
// ===========================================================================

export const getFileNames = (tree: any[], values: string[]): string[] => {
  if (!values) return [];
  const labels: string[] = [];
  const traverse = (nodes: any[]) => {
    for (const node of nodes) {
      if (values.includes(node.key)) {
        labels.push(node.title);
      }
      if (node.children) {
        traverse(node.children);
      }
    }
  };
  traverse(tree);
  values.forEach((value) => {
    if (value.startsWith('http')) {
      labels.push(
        value
          .substring(value.lastIndexOf('/') + 1)
          .substring(value.substring(value.lastIndexOf('/') + 1).indexOf('-') + 1),
      );
    }
  });
  return labels;
};

export const getFileInfo = (
  nodes: any[] | null,
  keys: string[],
): { key: string; fileName: string; isFile: boolean }[] => {
  if (!nodes || !Array.isArray(nodes) || keys.length === 0) return [];

  const keySet = new Set(keys);
  const seen = new Set<string>();
  const fileInfo: { key: string; fileName: string; isFile: boolean }[] = [];

  const traverse = (nodes: any[]) => {
    nodes.forEach((node) => {
      // A file can be reachable from more than one root (e.g. a favourited vault
      // file is in both the vault tree and the Favourites list). Report it once.
      if (keySet.has(node.key) && !seen.has(node.key)) {
        seen.add(node.key);
        fileInfo.push({
          key: node.key,
          fileName: node.title,
          isFile: node.type === 'file',
        });
      }
      if (node.children) {
        traverse(node.children);
      }
    });
  };

  traverse(nodes);
  return fileInfo;
};

export const DownloadFile = async (fileUrl: string, fileName: string) => {
  window.dispatchEvent(new CustomEvent('download-start'));

  try {
    const token = localStorage.getItem('token');
    const response = await fetch(fileUrl, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) throw new Error('Network Error');

    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    message.error(
      getIntl().formatMessage({
        id: 'pages.file.prompt.downloadFail',
        defaultMessage: 'Failed to download',
      }),
    );
  } finally {
    window.dispatchEvent(new CustomEvent('download-end'));
  }
};

// ===========================================================================
// MEDIA CONSTANTS & CONFIGURATIONS
// ===========================================================================

export const fallbackImg =
  'data:image/png;base64,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';

export const videoPreview =
  'data:image/png;base64,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';

export const imageDraggerProps = {
  accept: 'image/*',
  multiple: false,
  showUploadList: false,
  action: upload,
  headers: {
    authorization: `Bearer ${localStorage.getItem('token')}`,
  },
} as UploadProps;

export const videoDraggerProps = {
  accept: 'video/*',
  multiple: false,
  showUploadList: false,
  action: upload,
  headers: {
    authorization: `Bearer ${localStorage.getItem('token')}`,
  },
} as UploadProps;

export const fileDraggerProps = {
  accept: '.pdf, .doc, .docx, .xls, .xlsx, .xslx, .pptx, .ppt',
  multiple: false,
  showUploadList: false,
  action: upload,
  headers: {
    authorization: `Bearer ${localStorage.getItem('token')}`,
  },
} as UploadProps;

// ===========================================================================
// FILE VIEWER COMPONENTS
// ===========================================================================

const WORD_NOTE_REF_ID = /^(?:footnote|endnote)-ref-/;

const FOOTNOTE_MARK_BRACKETS = /(<span class="docx-footnote-ref">)\[(\d+)\](<\/span>)/g;

export function sanitizeDocxHtml(html: string): string {
  const sanitized = Sanitize(html, {
    allowedTags: Sanitize.defaults.allowedTags
      .filter((tag) => tag !== 'a')
      .concat(['img', 'span', 'sup', 'sub', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
    // Links are dropped, but a footnote mark must stay recognisable so the
    // citation highlighter can skip it.
    transformTags: {
      a: (tagName, attribs) =>
        WORD_NOTE_REF_ID.test(attribs.id ?? '')
          ? { tagName: 'span', attribs: { class: 'docx-footnote-ref' } }
          : { tagName, attribs },
    },
    allowedAttributes: {
      ...Sanitize.defaults.allowedAttributes,
      '*': ['style', 'class'],
      img: ['src', 'alt', 'width', 'height'],
    },
    allowedSchemesByTag: { img: ['data'] },
  });
  // Word shows a footnote mark as a raised "1"; mammoth writes "[1]".
  return sanitized.replace(FOOTNOTE_MARK_BRACKETS, '$1$2$3');
}

interface FileViewerWithTokenProps {
  filePath: string;
  fileType: string;
}

export const FileViewerWithToken: FC<FileViewerWithTokenProps> = ({ filePath }) => {
  const intl = useIntl();
  const [docxHtml, setDocxHtml] = useState<string | null>(null);
  const token = localStorage.getItem('token');
  useEffect(() => {
    const fetchFile = async () => {
      try {
        const response = await fetch(filePath, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch file');
        }

        const blob = await response.blob();
        const arrayBuffer = await blob.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        setDocxHtml(sanitizeDocxHtml(result.value));
      } catch (err) {
        message.error(
          intl.formatMessage({
            id: 'components.fileViewerWithToken.retrieveFailedError',
            defaultMessage: 'Failed to retrieve file',
          }),
        );
      }
    };

    fetchFile();
  }, [filePath, token]);

  if (!docxHtml)
    return <div>{intl.formatMessage({ id: 'common.loading', defaultMessage: 'Loading...' })}</div>;

  return (
    <div className="file-viewer-with-title-font">
      <DocxViewerWithHighlight docxHtml={docxHtml} height="100%" />
    </div>
  );
};

// ===========================================================================
// FILE VIEWER COMPONENTS (continued)
// ===========================================================================

export const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Strip HTML tags and decode common entities to get plain text for matching. */
export const stripHtmlTags = (s: string) =>
  s
    .replace(/^[^<]*>/, '') // remove unclosed tag fragment at the start
    .replace(/<[^>]*$/, '') // remove unclosed tag fragment at the end
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim();

interface MsgRecipient {
  name?: string;
  email?: string;
}

interface MsgFileData {
  subject?: string;
  senderName?: string;
  senderEmail?: string;
  body?: string;
  headers?: string;
  recipients?: MsgRecipient[];
  attachments?: { fileName?: string; fileNameShort?: string }[];
}

/** Build an HTML string for a .msg file (header + plain-text body in <pre>). */
function buildMsgHtml(data: MsgFileData): string {
  const sender =
    data.senderName && data.senderEmail
      ? `${data.senderName} <${data.senderEmail}>`
      : data.senderName || data.senderEmail || '';
  const recipients = (data.recipients ?? [])
    .map((r) => (r.name && r.email ? `${r.name} <${r.email}>` : r.name || r.email || ''))
    .filter(Boolean)
    .join(', ');
  const attachments = (data.attachments ?? [])
    .map((a) => a.fileName || a.fileNameShort)
    .filter(Boolean)
    .join(', ');

  const hdr: string[] = [];
  hdr.push('<div class="email-header">');
  if (data.subject) hdr.push(`<h3>${escapeHtml(data.subject)}</h3>`);
  if (sender) hdr.push(`<div><strong>From:</strong> ${escapeHtml(sender)}</div>`);
  if (recipients) hdr.push(`<div><strong>To:</strong> ${escapeHtml(recipients)}</div>`);
  if (attachments) hdr.push(`<div><strong>Attachments:</strong> ${escapeHtml(attachments)}</div>`);
  hdr.push('</div>');

  const body = escapeHtml(data.body || '');
  return `${hdr.join('')}<pre class="email-body-pre">${body}</pre>`;
}

/** Build an HTML string for a .eml file (header + sanitized HTML or plain-text body). */
function buildEmlHtml(parsed: Awaited<ReturnType<typeof PostalMime.parse>>): string {
  const fromAddr = parsed.from?.address
    ? parsed.from.name
      ? `${parsed.from.name} <${parsed.from.address}>`
      : parsed.from.address
    : '';
  const toAddrs = (parsed.to || [])
    .map((r) => (r.name ? `${r.name} <${r.address}>` : r.address || ''))
    .filter(Boolean)
    .join(', ');
  const ccAddrs = (parsed.cc || [])
    .map((r) => (r.name ? `${r.name} <${r.address}>` : r.address || ''))
    .filter(Boolean)
    .join(', ');
  const attachmentNames = (parsed.attachments || [])
    .map((a) => a.filename)
    .filter(Boolean)
    .join(', ');

  const hdr: string[] = [];
  hdr.push('<div class="email-header">');
  if (parsed.subject) hdr.push(`<h3>${escapeHtml(parsed.subject)}</h3>`);
  if (fromAddr) hdr.push(`<div><strong>From:</strong> ${escapeHtml(fromAddr)}</div>`);
  if (toAddrs) hdr.push(`<div><strong>To:</strong> ${escapeHtml(toAddrs)}</div>`);
  if (ccAddrs) hdr.push(`<div><strong>Cc:</strong> ${escapeHtml(ccAddrs)}</div>`);
  if (parsed.date) hdr.push(`<div><strong>Date:</strong> ${escapeHtml(parsed.date)}</div>`);
  if (attachmentNames)
    hdr.push(`<div><strong>Attachments:</strong> ${escapeHtml(attachmentNames)}</div>`);
  hdr.push('</div>');

  const rawBody = parsed.html || parsed.text || '';
  const isHtmlBody = /<\/?[a-z][\s\S]*>/i.test(rawBody);
  let bodyHtml: string;
  if (isHtmlBody) {
    bodyHtml = Sanitize(rawBody, {
      allowedTags: Sanitize.defaults.allowedTags
        .filter((tag) => tag !== 'a')
        .concat(['img', 'span', 'sup', 'sub', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
      allowedAttributes: {
        ...Sanitize.defaults.allowedAttributes,
        '*': ['style', 'class'],
        img: ['src', 'alt', 'width', 'height'],
      },
      allowedSchemesByTag: { img: ['data'] },
    });
  } else {
    bodyHtml = `<pre class="email-body-pre">${escapeHtml(rawBody)}</pre>`;
  }

  return hdr.join('') + bodyHtml;
}

interface IFrameWithTokenProps {
  filePath?: string;
  fileId?: string;
  page?: number;
  height?: string;
  highlight?: string;
  preferPdfIframe?: boolean;
  fullPdfPreview?: boolean;
  /** When true and the file is a PDF, render via server-side page extraction instead of pdf.js. */
  serverRenderPage?: boolean;
  /** Optional context for disambiguating among multiple occurrences */
  highlightContext?: { contextBefore?: string; contextAfter?: string };
  /** Sentence text to scroll to (separate from highlight keyword) */
  scrollTarget?: string;
  /** Enable keyword-fallback highlighting in full preview (default: true) */
  highlightAll?: boolean;
  /** Called once the viewer determines match type: exact (salmon) vs fuzzy (green) vs none */
  onScrollMatchType?: (type: 'exact' | 'fuzzy' | 'none') => void;
  /** Pre-computed match type from parent (answer vs text_snippet comparison) — overrides viewer's internal determination */
  scrollMatchType?: 'exact' | 'fuzzy' | 'none';
  /** Override scroll-target overlay color while retaining chained/fuzzy matching. */
  scrollTargetColor?: string;
  /** Zero-based keyword match to scroll to when all matches are highlighted */
  highlightMatchIndex?: number;
  /** Limit scroll-target matching to the selected page and its continuation page. */
  scrollTargetPageOnly?: boolean;
}

export function buildPageRenderUrl(fetchUrl: string, page: number | undefined): string {
  return `/api/files/page-render/?file_url=${encodeURIComponent(fetchUrl)}&page=${page ?? 1}`;
}

export async function _fetchServerRenderedPage(
  fetchUrl: string,
  page: number | undefined,
  token: string | null,
): Promise<string | null> {
  try {
    const renderUrl = buildPageRenderUrl(fetchUrl, page);
    const r = await fetch(renderUrl, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return null;
    const blob = await r.blob();
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

export interface BlobRouteOptions {
  isDocx: boolean;
  isMsg: boolean;
  isEml: boolean;
  isText: boolean;
  page: number | undefined;
  preferPdfIframe: boolean;
}

export async function _routeBlobContent(
  blob: Blob,
  filePath: string,
  opts: BlobRouteOptions,
  iframeRef: React.RefObject<HTMLIFrameElement>,
  setDocxRawHtml: (html: string) => void,
  setPdfBlobUrl: (url: string) => void,
): Promise<void> {
  if (opts.isDocx) {
    const arrayBuffer = await blob.arrayBuffer();
    const [result, headerFooterHtml] = await Promise.all([
      mammoth.convertToHtml({ arrayBuffer }),
      readDocxHeaderFooterHtml(arrayBuffer),
    ]);
    setDocxRawHtml(sanitizeDocxHtml(result.value + headerFooterHtml));
    return;
  }
  if (opts.isMsg) {
    const arrayBuffer = await blob.arrayBuffer();
    const reader = new MsgReader(arrayBuffer);
    setDocxRawHtml(buildMsgHtml(reader.getFileData() as MsgFileData));
    return;
  }
  if (opts.isEml) {
    const text = await blob.text();
    const parsed = await PostalMime.parse(text);
    setDocxRawHtml(buildEmlHtml(parsed));
    return;
  }
  if (opts.isText) {
    const text = await blob.text();
    const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    setDocxRawHtml(`<pre class="text-file-pre">${escaped}</pre>`);
    return;
  }
  const blobUrl = URL.createObjectURL(blob);
  const isPdf = (filePath ?? '').split('.').pop()?.toLowerCase() === 'pdf';
  if (isPdf) {
    if (opts.preferPdfIframe) {
      if (iframeRef.current) {
        iframeRef.current.src = `${blobUrl}#page=${opts.page ?? 1}&zoom=page-fit`;
      }
    } else {
      setPdfBlobUrl(blobUrl);
    }
    return;
  }
  if (iframeRef.current) iframeRef.current.src = `${blobUrl}#page=${opts.page ?? 1}&zoom=page-fit`;
}

export const IFrameWithToken: React.FC<IFrameWithTokenProps> = ({
  filePath,
  fileId,
  page,
  height,
  highlight,
  preferPdfIframe = false,
  fullPdfPreview = false,
  serverRenderPage = false,
  highlightContext,
  scrollTarget,
  highlightAll = true,
  onScrollMatchType,
  scrollMatchType,
  scrollTargetColor,
  highlightMatchIndex,
  scrollTargetPageOnly = false,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [docxRawHtml, setDocxRawHtml] = useState<string | null>(null);
  const [docxHtml, setDocxHtml] = useState<string | null>(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [serverPdfBlobUrl, setServerPdfBlobUrl] = useState<string | null>(null);
  const token = localStorage.getItem('token');
  const fileExt = filePath?.split('.').pop()?.toLowerCase();
  const isEmailFile = fileExt === 'msg' || fileExt === 'eml';
  const fileLoadPage = serverRenderPage || preferPdfIframe ? page : undefined;
  const effectiveHighlight = isEmailFile && highlight ? stripHtmlTags(highlight) : highlight;
  const effectiveScrollTarget =
    isEmailFile && scrollTarget ? stripHtmlTags(scrollTarget) : scrollTarget;

  useEffect(() => {
    const loadFile = async () => {
      try {
        const fetchUrl = fileId ? `/api/files/${fileId}` : filePath;
        if (!fetchUrl) {
          console.error('No file path or file ID provided');
          return;
        }

        const fileExtension = filePath?.split('.').pop()?.toLowerCase();
        const textExtensions = [
          'txt',
          'log',
          'md',
          'json',
          'xml',
          'csv',
          'html',
          'css',
          'js',
          'ts',
          'tsx',
          'jsx',
        ];
        let isText = textExtensions.includes(fileExtension || '');
        const isDocx = fileExtension === 'docx' || fileExtension === 'doc';
        const isMsg = fileExtension === 'msg';
        const isEml = fileExtension === 'eml';

        if (serverRenderPage && fileExt === 'pdf') {
          setServerPdfBlobUrl(null);
          const blobUrl = await _fetchServerRenderedPage(fetchUrl, fileLoadPage, token);
          if (blobUrl) {
            setServerPdfBlobUrl(blobUrl);
            return;
          }
        }

        const res = await fetch(fetchUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) throw new Error('Fetch failed');

        if (fileId && !isText && !isDocx && !isMsg && !isEml) {
          const contentType = res.headers.get('content-type') || '';
          isText = contentType.startsWith('text/');
        }

        const blob = await res.blob();
        await _routeBlobContent(
          blob,
          filePath ?? '',
          { isDocx, isMsg, isEml, isText, page: fileLoadPage, preferPdfIframe },
          iframeRef,
          setDocxRawHtml,
          setPdfBlobUrl,
        );
      } catch (err) {
        console.error('Error loading file into iframe:', err);
      }
    };

    loadFile();
  }, [filePath, fileId, token, serverRenderPage, fileLoadPage, preferPdfIframe]);

  useEffect(() => {
    if (docxRawHtml === null) return;
    if (effectiveScrollTarget) {
      const exactCount = countScrollTargetMatches(docxRawHtml, effectiveScrollTarget);
      const EXACT_COLOR = 'rgba(250, 163, 138, 0.55)';
      const FUZZY_COLOR = 'rgba(134, 239, 172, 0.55)';
      const internalType = exactCount > 0 ? 'exact' : 'fuzzy';
      const effectiveType = scrollMatchType ?? internalType;
      const color = scrollTargetColor ?? (effectiveType === 'fuzzy' ? FUZZY_COLOR : EXACT_COLOR);
      let scrollHtml: string;
      if (exactCount > 0) {
        scrollHtml = highlightAllScrollTargetsInHtml(docxRawHtml, effectiveScrollTarget, color);
      } else {
        scrollHtml = addFuzzyHighlightToHtml(docxRawHtml, effectiveScrollTarget, color);
      }
      onScrollMatchType?.(internalType);
      setDocxHtml(
        effectiveHighlight ? highlightAllInHtml(scrollHtml, effectiveHighlight) : scrollHtml,
      );
    } else if (effectiveHighlight && highlightAll && highlightMatchIndex !== undefined) {
      setDocxHtml(highlightAllInHtml(docxRawHtml, effectiveHighlight));
    } else if (effectiveHighlight) {
      setDocxHtml(highlightTextInHtml(docxRawHtml, effectiveHighlight, highlightContext));
    } else {
      setDocxHtml(docxRawHtml);
    }
  }, [
    docxRawHtml,
    effectiveHighlight,
    highlightAll,
    highlightContext,
    effectiveScrollTarget,
    onScrollMatchType,
    scrollMatchType,
    scrollTargetColor,
    highlightMatchIndex,
  ]);

  // DocxViewerWithHighlight is used for docx, eml, msg, txt, and md files
  if (docxHtml !== null) {
    return (
      <DocxViewerWithHighlight
        docxHtml={docxHtml}
        height={height || '100%'}
        scrollTarget={effectiveScrollTarget}
        hasHighlight={!!effectiveHighlight}
        highlightMatchIndex={highlightMatchIndex}
        isEml={fileExt === 'eml'}
      />
    );
  }

  if (pdfBlobUrl) {
    if (scrollTarget || fullPdfPreview) {
      return (
        <SearchPdfViewerWithHighlight
          blobUrl={pdfBlobUrl}
          page={page || 1}
          highlight={highlight}
          height={height || '100%'}
          scrollTarget={scrollTarget}
          highlightAll={highlightAll}
          onScrollMatchType={onScrollMatchType}
          scrollMatchType={scrollMatchType}
          scrollTargetColor={scrollTargetColor}
          highlightMatchIndex={highlightMatchIndex}
          scrollTargetPageOnly={scrollTargetPageOnly}
        />
      );
    }
    return (
      <PdfViewerWithHighlight
        blobUrl={pdfBlobUrl}
        page={page || 1}
        highlight={highlight}
        height={height || '100%'}
      />
    );
  }

  if (serverPdfBlobUrl) {
    if (scrollTarget || fullPdfPreview) {
      return (
        <SearchPdfViewerWithHighlight
          blobUrl={serverPdfBlobUrl}
          page={1}
          height={height ?? '100%'}
          highlight={highlight}
          scrollTarget={scrollTarget}
          highlightAll={highlightAll}
          scrollTargetColor={scrollTargetColor}
          highlightMatchIndex={highlightMatchIndex}
          scrollTargetPageOnly={scrollTargetPageOnly}
        />
      );
    }
    return (
      <PdfViewerWithHighlight
        blobUrl={serverPdfBlobUrl}
        page={1}
        highlight={highlight}
        height={height ?? '100%'}
      />
    );
  }

  return (
    <div
      style={{
        width: '100%',
        height: height || '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <img src="/loading.svg" alt="Loading" style={{ width: 100, height: 100 }} />
    </div>
  );
};

// ===========================================================================
// UI COMPONENTS
// ===========================================================================

export const PlatformTitle: React.FC<{ text: string | React.ReactElement; onClick?: any }> = ({
  text,
  onClick,
}) => {
  return (
    <Card
      style={{
        backgroundImage: 'url(/welcome_background.jpeg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        width: 'fit-content',
        border: 0,
        marginBlockEnd: 12,
        cursor: onClick ? 'pointer' : 'default',
        fontSize: 16,
      }}
      styles={{ body: { padding: '4px 12px' } }}
      onClick={onClick}
    >
      <Title level={5} className="titleFont" style={{ margin: 0 }}>
        {text}
      </Title>
    </Card>
  );
};
